// src/pages/customer/CustomerReceipt.jsx
// Чек покупки у клиента: после скана QR «Оплачено» и из истории магазина.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { loyalty as loyaltyApi } from '../../api';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л') return 'л';
  return 'шт';
}

const fmtQty = (q) => String(Math.round(Number(q || 0) * 1000) / 1000).replace('.', ',');

export const receiptDate = (d) => new Date(d).toLocaleString('ru', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// Карточка чека — общая для экрана скана и страницы чека
export function ReceiptCard({ receipt }) {
  const { t } = useTranslation();
  if (!receipt) return null;
  const cur = receipt.shop?.currency;
  const items = receipt.items || [];
  const itemsDisc = items.reduce((s, i) => s + (Number(i.discountAmount) || 0), 0);
  const gross = items.reduce((s, i) => s + Number(i.qty) * Number(i.salePrice), 0);
  const totalDisc = itemsDisc + (Number(receipt.discountAmount) || 0);
  const method = receipt.paidByPoints ? 'points' : (receipt.payMethod || 'cash');

  const row = (label, value, strong, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginTop: 6 }}>
      <span style={{ fontSize: strong ? 15 : 13, fontWeight: strong ? 800 : 500, color: strong ? 'var(--fg)' : 'var(--fg-muted)' }}>{label}</span>
      <span style={{ fontSize: strong ? 20 : 13, fontWeight: strong ? 800 : 600, color: color || 'var(--fg)', fontVariantNumeric: 'tabular-nums', fontFamily: strong ? 'var(--font-display)' : undefined }}>{value}</span>
    </div>
  );

  return (
    <div className="card" style={{ padding: 16, textAlign: 'left' }}>
      <div style={{ textAlign: 'center', paddingBottom: 12, borderBottom: '1px dashed var(--border)' }}>
        <p style={{ fontSize: 17, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{receipt.shop?.name}</p>
        <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{receiptDate(receipt.createdAt)}</p>
      </div>

      <div style={{ padding: '10px 0', borderBottom: '1px dashed var(--border)' }}>
        {items.map(i => {
          const lineBase = Number(i.qty) * Number(i.salePrice);
          const disc = Number(i.discountAmount) || 0;
          const name = i.noName && !i.name ? t('receipt.noName') : i.name;
          return (
            <div key={i.id} style={{ padding: '6px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <p style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                  {name}{i.variantName ? <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}> · {i.variantName}</span> : null}
                </p>
                <p style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {formatMoney(lineBase - disc, cur)}
                </p>
              </div>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                {fmtQty(i.qty)} {unitLabel(i.unit)} × {formatMoney(i.salePrice, cur)}
                {disc > 0 && <span style={{ color: 'var(--brand-accent)' }}> · {t('receipt.discount')} −{formatMoney(disc, cur)}</span>}
              </p>
            </div>
          );
        })}
      </div>

      <div style={{ paddingTop: 6 }}>
        {totalDisc > 0 && row(t('receipt.subtotal'), formatMoney(gross, cur))}
        {totalDisc > 0 && row(t('receipt.discount'), `−${formatMoney(totalDisc, cur)}`, false, 'var(--brand-accent)')}
        {row(t('receipt.total'), formatMoney(receipt.total, cur), true)}
        {row(t('receipt.payMethod'), t(`receipt.pay_${method}`, method))}
        {receipt.paidByPoints && receipt.pointsRedeemed > 0 && row(t('receipt.pointsRedeemed'), `−${receipt.pointsRedeemed} ⭐`, false, 'var(--brand-accent)')}
        {receipt.paidByPoints && receipt.cashAmount > 0 && row(t('receipt.cashTopUp'), formatMoney(receipt.cashAmount, cur))}
        {receipt.creditAmount > 0 && row(t('receipt.onCredit'), formatMoney(receipt.creditAmount, cur), false, 'var(--danger)')}
        {receipt.pointsEarned > 0 && row(t('receipt.pointsEarned'), `+${receipt.pointsEarned} ⭐`, false, 'var(--success)')}
      </div>
    </div>
  );
}

// Строка списка чеков (в карте магазина)
export function ReceiptRow({ r, onClick }) {
  const { t } = useTranslation();
  return (
    <div onClick={onClick} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon name="fileText" size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(r.total, r.shop?.currency)}</p>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
          {receiptDate(r.createdAt)} · {t('receipt.itemsCount', { count: r.itemsCount })}
        </p>
      </div>
      {r.pointsEarned > 0 && (
        <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>+{r.pointsEarned}</p>
      )}
      <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
    </div>
  );
}

export default function CustomerReceipt() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    loyaltyApi.receipt(id)
      .then(r => { if (alive) setReceipt(r); })
      .catch(e => { if (alive) setError(e.message || t('receipt.notFound')); });
    return () => { alive = false; };
  }, [id, t]);

  return (
    <div className="page" style={{ paddingBottom: 24 }}>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{t('receipt.title')}</h1>
      </div>
      <div style={{ padding: '0 16px', maxWidth: 520, margin: '0 auto' }}>
        {error ? (
          <div className="empty-state"><p>{error}</p></div>
        ) : !receipt ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <ReceiptCard receipt={receipt} />
        )}
      </div>
    </div>
  );
}
